import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Eye, Users, TrendingUp, Globe } from "lucide-react";

function KpiCard({ label, primary, secondary, Icon, color }) {
  return (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="overline" style={{ fontSize: "0.6rem" }}>{label}</div>
        {Icon && <Icon size={16} strokeWidth={1.75} style={{ color }} />}
      </div>
      <div className="font-serif-display text-3xl lg:text-4xl leading-none" style={{ color: "var(--ink)" }}>
        {primary}
      </div>
      <p className="mt-2 text-xs" style={{ color: "var(--muted)" }}>{secondary}</p>
    </div>
  );
}

function Sparkline({ data }) {
  // data: [{date, views, uniques}]
  const maxV = Math.max(1, ...data.map((d) => d.views));
  const w = 800;
  const h = 160;
  const pad = 10;
  const xStep = (w - pad * 2) / Math.max(1, data.length - 1);

  const linePoints = data.map((d, i) => {
    const x = pad + i * xStep;
    const y = h - pad - (d.views / maxV) * (h - pad * 2);
    return [x, y];
  });
  const pathD = linePoints.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
  const areaD = `${pathD} L${linePoints[linePoints.length - 1][0]},${h - pad} L${linePoints[0][0]},${h - pad} Z`;

  return (
    <div className="card-surface p-6">
      <div className="overline mb-4">Last 30 days · daily page views</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" preserveAspectRatio="none">
        <defs>
          <linearGradient id="spark-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--ember)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="var(--ember)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#spark-grad)" />
        <path d={pathD} stroke="var(--ember)" strokeWidth="2" fill="none" />
        {linePoints.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="2.5" fill="var(--ember)" data-testid={`spark-pt-${i}`}>
            <title>{`${data[i].date} — ${data[i].views} views, ${data[i].uniques} unique`}</title>
          </circle>
        ))}
      </svg>
      <div className="mt-3 flex justify-between text-[0.6rem] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/admin/analytics")
      .then((r) => setStats(r.data))
      .catch(() => setError("Could not load analytics"))
      .finally(() => setLoading(false));
  }, []);

  const trendVsPrev = useMemo(() => {
    if (!stats?.daily || stats.daily.length < 14) return null;
    const last7 = stats.daily.slice(-7).reduce((a, d) => a + d.views, 0);
    const prev7 = stats.daily.slice(-14, -7).reduce((a, d) => a + d.views, 0);
    if (prev7 === 0) return last7 > 0 ? "+∞" : "—";
    const pct = Math.round(((last7 - prev7) / prev7) * 100);
    return `${pct >= 0 ? "+" : ""}${pct}%`;
  }, [stats]);

  if (loading) return <p style={{ color: "var(--muted)" }}>Loading analytics…</p>;
  if (error) return <p style={{ color: "var(--terracotta)" }}>{error}</p>;
  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div>
        <h2 className="font-serif-display text-2xl lg:text-3xl mb-1" style={{ color: "var(--ink)" }}>
          Who's reading
        </h2>
        <p className="text-xs" style={{ color: "var(--muted)" }}>
          Your own visits are excluded. Bots and link-preview crawlers are filtered out. "Unique" = distinct visitor in the window.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Today"
          primary={stats.today.views.toLocaleString()}
          secondary={`${stats.today.uniques.toLocaleString()} unique visitors`}
          Icon={Eye}
          color="var(--ember)"
        />
        <KpiCard
          label="Last 7 days"
          primary={stats.week.views.toLocaleString()}
          secondary={`${stats.week.uniques.toLocaleString()} unique visitors${trendVsPrev ? ` · ${trendVsPrev} vs. prior 7d` : ""}`}
          Icon={TrendingUp}
          color="var(--terracotta)"
        />
        <KpiCard
          label="Last 30 days"
          primary={stats.month.views.toLocaleString()}
          secondary={`${stats.month.uniques.toLocaleString()} unique visitors`}
          Icon={Users}
          color="var(--olive)"
        />
        <KpiCard
          label="All time"
          primary={stats.all_time.views.toLocaleString()}
          secondary={`${stats.all_time.uniques.toLocaleString()} unique visitors`}
          Icon={Globe}
          color="var(--gold)"
        />
      </div>

      {stats.daily && stats.daily.length > 0 && <Sparkline data={stats.daily} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top pages */}
        <div className="card-surface p-6">
          <div className="overline mb-4">Top pages · last 30 days</div>
          {stats.top_pages.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--muted)" }}>No visits yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[0.6rem] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                  <th className="text-left pb-2">Path</th>
                  <th className="text-right pb-2">Views</th>
                  <th className="text-right pb-2">Unique</th>
                </tr>
              </thead>
              <tbody>
                {stats.top_pages.map((p) => (
                  <tr key={p.path} className="border-t" style={{ borderColor: "var(--border-subtle)" }} data-testid={`analytics-page-${p.path.replace(/\//g, "_")}`}>
                    <td className="py-2 pr-3 truncate max-w-[20rem]" style={{ color: "var(--ink-2)" }}>{p.path}</td>
                    <td className="py-2 text-right" style={{ color: "var(--ember)" }}>{p.views.toLocaleString()}</td>
                    <td className="py-2 text-right" style={{ color: "var(--ink-2)" }}>{p.uniques.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Top referrers */}
        <div className="card-surface p-6">
          <div className="overline mb-4">Top referrers · last 30 days</div>
          {stats.top_referrers.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--muted)" }}>No referrers yet. Most visitors arrived directly.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[0.6rem] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                  <th className="text-left pb-2">Came from</th>
                  <th className="text-right pb-2">Visits</th>
                </tr>
              </thead>
              <tbody>
                {stats.top_referrers.map((r, i) => {
                  let host = r.referrer;
                  try { host = new URL(r.referrer).hostname; } catch {}
                  return (
                    <tr key={i} className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
                      <td className="py-2 pr-3 truncate max-w-[20rem]" style={{ color: "var(--ink-2)" }}>{host}</td>
                      <td className="py-2 text-right" style={{ color: "var(--ember)" }}>{r.views.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
